import * as React from "react";
import {
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography
} from "../../../components/mui";
import Modal from "../../../components/shared/Modal";
import { useToast } from "../../../components/shared/ToastProvider";

import "../../../styles/main.css";
import "../../../styles/pages/condoUnitDetail.css";

// UI-only form for creating/updating a utility payment.
export default function UtilityPaymentFormModal({
  open,
  onClose,
  mode = "add", // "add" | "edit"
  initialValues = {}
}) {
  const isEdit = mode === "edit";
  const { showToast } = useToast();

  const handlePrimaryClick = () => {
    showToast({
      message: isEdit ? "Utility payment updated" : "Utility payment created",
      severity: "success"
    });
    if (onClose) onClose();
  };

  const actions = (
    <>
      <Button variant="outlined" onClick={onClose}>
        Cancel
      </Button>
      <Button variant="contained" onClick={handlePrimaryClick}>
        {isEdit ? "Update" : "Create"}
      </Button>
    </>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? "Edit Utility Payment" : "Add Utility Payment"}
      actions={actions}
      maxWidth="md"
      fullWidth
    >
      <Box className="modal-form-grid modal-form-grid--spaced">
        {/* Condo Company */}
        <Box className="modal-form-row">
          <FormControl fullWidth required size="small">
            <InputLabel>Condo Company</InputLabel>
            <Select
              defaultValue={initialValues.condoCompany || "Caspo"}
              label="Condo Company"
            >
              <MenuItem value="Caspo">Caspo</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Condo Unit */}
        <Box className="modal-form-row">
          <FormControl fullWidth required size="small">
            <InputLabel>Condo Unit</InputLabel>
            <Select
              defaultValue={initialValues.condoUnit || "Three Central - 28F Three Central"}
              label="Condo Unit"
            >
              <MenuItem value="Three Central - 28F Three Central">Three Central - 28F Three Central</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Utility Type */}
        <Box className="modal-form-row">
          <FormControl fullWidth required size="small">
            <InputLabel>Utility Type</InputLabel>
            <Select
              defaultValue={initialValues.utilityType || "Electricity"}
              label="Utility Type"
            >
              <MenuItem value="Electricity">Electricity</MenuItem>
              <MenuItem value="Water">Water</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Account Name / Account Number / Amount (same line) */}
        <Box className="modal-form-row" display="flex" gap={2}>
          <Box flex={1} display="flex" flexDirection="column">
            <Typography variant="caption" className="utility-account-label">
              Account Name
            </Typography>
            <Typography variant="body2" className="utility-account-value">
              {initialValues.accountName || "Meralco"}
            </Typography>
          </Box>

          <Box flex={1} display="flex" flexDirection="column">
            <Typography variant="caption" className="utility-account-label">
              Account Number
            </Typography>
            <Typography variant="body2" className="utility-account-value">
              {initialValues.accountNo || "0"}
            </Typography>
          </Box>

          <Box flex={1} display="flex" flexDirection="column">
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body2">PHP</Typography>
              <TextField
                fullWidth
                required
                size="small"
                type="number"
                label="Amount"
                placeholder="0.00"
                defaultValue={initialValues.amountValue || "0.0000"}
                inputProps={{ step: "0.01" }}
              />
            </Box>
          </Box>
        </Box>

        {/* Official Receipt No / Billing No */}
        <Box className="modal-form-row" display="flex" gap={1}>
          <TextField
            fullWidth
            required
            size="small"
            label="Official Receipt No"
            defaultValue={initialValues.officialReceiptNo || ""}
          />
          <TextField
            fullWidth
            size="small"
            label="Billing No"
            defaultValue={initialValues.billingNo || ""}
          />
        </Box>

        {/* RF No / Check No */}
        <Box className="modal-form-row" display="flex" gap={1}>
          <FormControl fullWidth size="small">
            <InputLabel>RF No</InputLabel>
            <Select
              defaultValue={initialValues.rfNo || "U-2024-001"}
              label="RF No"
            >
              <MenuItem value="U-2024-001">U-2024-001</MenuItem>
              <MenuItem value="U-2024-002">U-2024-002</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            size="small"
            label="Check No"
            defaultValue={initialValues.checkNo || ""}
          />
        </Box>

        {/* Payment Date / Acct Entry Date / Payment From / Payment To */}
        <Box className="modal-form-row" display="flex" gap={1}>
          <TextField
            fullWidth
            size="small"
            type="date"
            label="Payment Date"
            InputLabelProps={{ shrink: true }}
            defaultValue={initialValues.paymentDateRaw || ""}
          />
          <TextField
            fullWidth
            required
            size="small"
            type="date"
            label="Acct Entry Date"
            InputLabelProps={{ shrink: true }}
            defaultValue={initialValues.acctEntryDateRaw || ""}
          />
          <TextField
            fullWidth
            required
            size="small"
            type="date"
            label="Payment From"
            InputLabelProps={{ shrink: true }}
            defaultValue={initialValues.paymentFromRaw || ""}
          />
          <TextField
            fullWidth
            required
            size="small"
            type="date"
            label="Payment To"
            InputLabelProps={{ shrink: true }}
            defaultValue={initialValues.paymentToRaw || ""}
          />
        </Box>

        {/* Current / Previous Reading */}
        <Box className="modal-form-row" display="flex" gap={1}>
          <TextField
            fullWidth
            size="small"
            label="Current Reading"
            defaultValue={initialValues.currentReading || ""}
          />
          <TextField
            fullWidth
            size="small"
            label="Previous Reading"
            defaultValue={initialValues.previousReading || ""}
          />
        </Box>

        {/* Payable To / Condo Sharing */}
        <Box className="modal-form-row" display="flex" gap={1}>
          <TextField
            fullWidth
            size="small"
            label="Payable To"
            defaultValue={initialValues.payableTo || "Meralco"}
          />
          <FormControl fullWidth required size="small">
            <InputLabel>Condo Sharing</InputLabel>
            <Select
              defaultValue={initialValues.condoSharing || "Not Shared"}
              label="Condo Sharing"
            >
              <MenuItem value="Not Shared">Not Shared</MenuItem>
              <MenuItem value="Shared">Shared</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Remarks */}
        <TextField
          fullWidth
          multiline
          rows={3}
          size="small"
          className="modal-form-row"
          label="Remarks"
          defaultValue={initialValues.remarks || ""}
        />
      </Box>
    </Modal>
  );
}
